import React, { useState, useEffect, useCallback } from "react";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { PreviewModal } from "../components/PreviewModal";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useAiContext } from "../ai/AiContext";
import { useToast } from "../components/Toast";
import { type BrandAsset } from "../utils/brandAssetImport";
import type { AttachmentMeta } from "../types/attachments";
import { useAuth } from "../context/AuthContext";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";
import { canEditModule } from "../config/roles";
import { IpMattersPage } from "./IpMattersPage";

import { db, serverTimestamp, storage } from "../../firebaseClient";
import { uploadBrandAssetFiles } from "../services/storageUpload";
import { analyzeUploadedFile } from "../services/aiService";

const safeArray = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

export function BrandAssetsPage() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, "BRAND_ASSETS") : false;

  const [activeTab, setActiveTab] = useState<"list" | "map" | "ip-rights">("list");
  const [items, setItems] = useState<BrandAsset[]>([]);
  const [filtered, setFiltered] = useState<BrandAsset[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BrandAsset | null>(null);
  const [previewItem, setPreviewItem] = useState<BrandAsset | null>(null);
  const [loading, setLoading] = useState(false);

  const { requestDelete } = useAiContext();
  const { showToast } = useToast();

  const fetchBrandAssets = useCallback(async () => {
    setLoading(true);
    try {
      const q = db.collection("brand_assets").orderBy("createdAt", "desc");
      const snapshot = await q.get();
      const list = snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as BrandAsset));
      setItems(list);
      setFiltered(list);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrandAssets();
  }, [fetchBrandAssets]);

  const handleDelete = (id: string, name: string) => {
    requestDelete({
      targetType: "brandAsset",
      targetId: id,
      summary: name,
      onConfirm: async () => {
        try {
          await db.collection("brand_assets").doc(id).delete();
          showToast("已刪除");
          fetchBrandAssets();
        } catch (e) {
          console.error(e);
          alert("刪除失敗");
        }
      },
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 16, flexShrink: 0 }}>
        <button className={`navbtn ${activeTab === "list" ? "active" : ""}`} onClick={() => setActiveTab("list")}>
          品牌資產列表
        </button>
        <button className={`navbtn ${activeTab === "map" ? "active" : ""}`} onClick={() => setActiveTab("map")}>
          品牌佈局導圖
        </button>
        <button className={`navbtn ${activeTab === "ip-rights" ? "active" : ""}`} onClick={() => setActiveTab("ip-rights")}>
          品牌資產申請
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "list" && (
          <>
            <DataToolbar canEdit={canEdit} onCreate={() => { setEditingItem(null); setIsModalOpen(true); }} onExport={() => {}} />
            <div style={{ display: "grid", gap: 10 }}>
              {filtered.map((row) => (
                <div key={row.id} className="tile" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: "bold", color: "#fff" }}>{row.name}</div>
                    <div className="p">
                      {row.type} | {row.owner} | {row.region}
                    </div>
                    <div style={{ fontSize: 11, color: row.status === "已到期" ? "var(--danger)" : "var(--ok)", marginTop: 4 }}>
                      狀態：{row.status}
                    </div>
                  </div>
                  <ActionButtons
                    canEdit={canEdit}
                    onPreview={() => setPreviewItem(row)}
                    onEdit={() => { setEditingItem(row); setIsModalOpen(true); }}
                    onDelete={() => handleDelete(row.id!, row.name)}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === "map" && <div className="p" style={{ textAlign: "center", padding: 40, color: "#666" }}>品牌佈局視覺化模組 (建置中)</div>}
        {activeTab === "ip-rights" && <IpMattersPage />}
      </div>

      {previewItem && <PreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
      {isModalOpen && (
        <AssetForm
          initialData={editingItem}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); fetchBrandAssets(); }}
        />
      )}
    </div>
  );
}

function AssetForm({ initialData, onClose, onSuccess }: any) {
  const { showToast } = useToast();
  const [form, setForm] = useState<any>(
    initialData || {
      group: "自有品牌",
      status: "生效中",
      relatedNos: {},
      attachments: [],
    }
  );
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleAiAnalyze = async () => {
    const targetFile = rawFiles[0];
    const existingFile = safeArray<AttachmentMeta>(form.attachments)[0];

    if (!targetFile && !existingFile) {
      showToast("請先上傳附件（PDF/圖片/Word）再啟動 AI 分析。", "error");
      return;
    }

    setAiLoading(true);
    try {
      const result: any = await analyzeUploadedFile({
	        featureId: "brandAsset.licenseCheck",
        file: targetFile,
        fileMeta: targetFile ? undefined : existingFile,
        mode: "analysis",
        documentType: "brand_asset",
	        rawFiles: rawFiles,
	        files: rawFiles,
        userInstruction:
          "請協助解析此品牌資產/智財附件（商標/著作權/專利/授權等），輸出『務實法務版 + 管理報告版』（管理報告版放上方），並以進攻談判角度列出授權/權利擴張/補強條款建議（若適用）。另請萃取可回填欄位：name(品牌/系列)、type(類型)、region(主要市場/來源)、expiryDate(到期日)、summary(摘要/注意事項)。請以繁中輸出，並盡量回傳欄位 key。",
      });

      const f: any = result?.extractedFields || result?.fields || {};

      const title = String(f.name || f.title || f.subject || "").trim();

	      const summary =
	        (typeof result?.summary === "string" ? result.summary : "") ||
	        (typeof result?.text === "string" ? result.text : "") ||
	        (typeof result?.reply === "string" ? result.reply : "") ||
	        (typeof result === "string" ? result : "");

	      const riskEvalText = String(result?.riskAssessmentText || "").trim();

      setForm((prev: any) => ({
        ...prev,
        name: prev.name || title || prev.name,
        type: prev.type || String(f.type || "").trim() || prev.type,
        region: prev.region || String(f.region || "").trim() || prev.region,
        expiryDate: prev.expiryDate || String(f.expiryDate || "").trim() || prev.expiryDate,
	        notes: prev.notes
          ? `${prev.notes}

[AI 摘要]
	${summary}${riskEvalText ? `\n\n${riskEvalText}` : ""}`.trim()
          : summary
          ? `[AI 摘要]
	${summary}${riskEvalText ? `\n\n${riskEvalText}` : ""}`.trim()
          : prev.notes,
      }));

      showToast("AI 解析完成：已填入可用內容（可再自行調整）");
    } catch (e) {
      console.error(e);
      showToast("AI 解析失敗，請確認檔案格式後再試一次", "error");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (form.expiryDate) {
      const today = new Date().toISOString().split("T")[0];
      const newStatus = form.expiryDate < today ? "已到期" : "生效中";
      if (form.status !== "已終止" && form.status !== "草稿" && newStatus !== form.status) {
        setForm((p: any) => ({ ...p, status: newStatus }));
      }
    }
  }, [form.expiryDate]);

  const deleteStorageFileIfAny = async (meta?: AttachmentMeta) => {
    const path = meta?.path || meta?.storagePath;
    if (!path) return;
    try {
      await storage.ref(path).delete();
    } catch (e) {
      console.warn("[BrandAssets] delete storage failed:", e);
    }
  };

  const handleRemoveRemoteFile = async (file: AttachmentMeta, index: number) => {
    const current = safeArray<AttachmentMeta>(form.attachments);
    const next = current.filter((_, i) => i !== index);
    setForm((p: any) => ({ ...p, attachments: next }));

    if (initialData?.id) {
      try {
        await deleteStorageFileIfAny(file);
        await db.collection("brand_assets").doc(initialData.id).update({
          attachments: next,
          updatedAt: serverTimestamp(),
        });
        showToast("附件已刪除");
      } catch (e) {
        console.error(e);
        alert("附件刪除失敗（DB/Storage）");
      }
    }
  };

  const handleDeleteAllAttachments = async () => {
    const current = safeArray<AttachmentMeta>(form.attachments);
    if (current.length === 0) return;

    if (!confirm("確定刪除全部附件？")) return;

    setForm((p: any) => ({ ...p, attachments: [] }));

    if (initialData?.id) {
      try {
        for (const f of current) await deleteStorageFileIfAny(f);
        await db.collection("brand_assets").doc(initialData.id).update({
          attachments: [],
          updatedAt: serverTimestamp(),
        });
        showToast("已刪除全部附件");
      } catch (e) {
        console.error(e);
        alert("刪除全部附件失敗");
      }
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.status || !form.effectiveDate) {
      return alert("請填寫必填欄位 (名稱、生效日、狀態)");
    }

    setSaving(true);
    try {
      const basePayload = { ...form, updatedAt: serverTimestamp() };
      const existingAttachments = safeArray<AttachmentMeta>(form.attachments);

      let docId = initialData?.id as string | undefined;

      if (!docId) {
        const docRef = await db.collection("brand_assets").add({
          ...basePayload,
          attachments: existingAttachments,
          createdAt: serverTimestamp(),
        });
        docId = docRef.id;
      } else {
        await db.collection("brand_assets").doc(docId).update({
          ...basePayload,
          attachments: existingAttachments,
        });
      }

      if (rawFiles.length > 0) {
        const uploaded = await uploadBrandAssetFiles(docId!, rawFiles);
        const merged = [...existingAttachments, ...uploaded];

        await db.collection("brand_assets").doc(docId!).update({
          attachments: merged,
          updatedAt: serverTimestamp(),
        });

        setForm((p: any) => ({ ...p, attachments: merged }));
        setRawFiles([]);
      }

      showToast("已儲存");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      alert("儲存/上傳失敗: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯品牌資產" : "新增品牌資產"} onClose={onClose} width={800}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">品牌／系列名稱 *</label>
            <input className="input" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div className="field">
            <label className="label">授權方 / 代理商</label>
            <input className="input" value={form.licensor ?? ""} onChange={(e) => setForm({ ...form, licensor: e.target.value })} placeholder="例: 迪士尼, 授權總公司" />
          </div>

          <div className="field">
            <label className="label">類型</label>
            <input className="input" value={form.type ?? ""} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="例: 商標, 著作權" />
          </div>

          <DateField label="生效日期 (必填) *" value={parseDate(form.effectiveDate || null)} onChange={(d) => setForm({ ...form, effectiveDate: formatDate(d) })} />
          <DateField label="到期日期" value={parseDate(form.expiryDate || null)} onChange={(d) => setForm({ ...form, expiryDate: formatDate(d) })} />

          <div className="field">
            <label className="label">適用地區 / 來源</label>
            <input className="input" value={form.region ?? ""} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="例: 台灣, 日本" />
          </div>

          <div className="field">
            <label className="label">目前狀態 *</label>
            <select
              className="input"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={{ color: form.status === "已到期" ? "var(--danger)" : "var(--ok)" }}
            >
              <option value="生效中">生效中</option>
              <option value="已到期">已到期</option>
              <option value="已終止">已終止</option>
              <option value="草稿">草稿</option>
              <option value="暫停">暫停</option>
            </select>
          </div>

          <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">備註 / 說明</label>
            <textarea className="input" rows={3} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="label" style={{ margin: 0, fontWeight: "bold" }}>相關證書或附件</div>
          <button
            className="smallbtn"
            style={{ color: "var(--danger)", borderColor: "var(--danger)", background: "rgba(255,93,108,0.1)" }}
            onClick={handleDeleteAllAttachments}
            type="button"
          >
            🗑 刪除全部附件
          </button>
        </div>

        <FileUploadKit
          label="相關證書或附件"
          existingFiles={safeArray<AttachmentMeta>(form.attachments)}
          rawFiles={rawFiles}
          onFilesSelected={(files) => setRawFiles((p) => [...p, ...files])}
          onRemoveRawFile={(idx) => setRawFiles((p) => p.filter((_, i) => i !== idx))}
          onRemoveFile={handleRemoveRemoteFile}
        />
      </div>

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 10 }}>
        <button
          className="btn"
          style={{ width: "auto", background: "transparent", border: "1px solid #666", color: "#fbbf24" }}
          onClick={handleAiAnalyze}
          disabled={saving || aiLoading || (rawFiles.length === 0 && safeArray<AttachmentMeta>(form.attachments).length === 0)}
          type="button"
        >
          {aiLoading ? "AI 分析中..." : "AI 快速分析"}
        </button>
        <button className="btn" style={{ width: "auto", background: "transparent", border: "1px solid #555", color: "#ccc" }} onClick={onClose} disabled={saving}>
          取消
        </button>
        <button className="btn" style={{ width: "auto" }} onClick={handleSave} disabled={saving}>
          {saving ? "處理中..." : "儲存資產"}
        </button>
      </div>
    </Modal>
  );
}
 